import React, { useRef, useEffect, useState } from "react";
import { Eraser, RotateCcw } from "lucide-react";

/**
 * Modern high-contrast Drawing Canvas with color palette, stroke width, and touch support.
 */
const DrawingCanvas = ({ drawing, onDrawingChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [strokeColor, setStrokeColor] = useState("#0f172a");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [isEraser, setIsEraser] = useState(false);

  const colors = [
    { name: "Dark Slate", value: "#0f172a" },
    { name: "Medical Blue", value: "#2563eb" },
    { name: "Crimson Red", value: "#dc2626" },
    { name: "Emerald Green", value: "#16a34a" },
  ];

  const strokeSizes = [
    { label: "Fine", value: 2 },
    { label: "Medium", value: 4 },
    { label: "Thick", value: 8 },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = isEraser ? "#ffffff" : strokeColor;

    if (drawing) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = drawing;
    } else {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [drawing, strokeColor, strokeWidth, isEraser]);

  const getCanvasCoordinates = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const coords = getCanvasCoordinates(
      e.type.includes("touch") ? e.touches[0].clientX : e.clientX,
      e.type.includes("touch") ? e.touches[0].clientY : e.clientY
    );
    setIsDrawing(true);
    setLastPos(coords);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCanvasCoordinates(
      e.type.includes("touch") ? e.touches[0].clientX : e.clientX,
      e.type.includes("touch") ? e.touches[0].clientY : e.clientY
    );

    ctx.beginPath();
    ctx.lineWidth = isEraser ? strokeWidth * 3 : strokeWidth;
    ctx.strokeStyle = isEraser ? "#ffffff" : strokeColor;
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    setLastPos(coords);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onDrawingChange(dataUrl);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onDrawingChange(null);
  };

  return (
    <div className="space-y-3 font-sans">
      {/* Canvas Toolbar Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
        {/* Colors */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">
            Color:
          </span>
          {colors.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => {
                setStrokeColor(c.value);
                setIsEraser(false);
              }}
              title={c.name}
              className={`w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center ${
                strokeColor === c.value && !isEraser
                  ? "border-slate-900 scale-110 shadow-sm"
                  : "border-transparent opacity-80 hover:opacity-100"
              }`}
              style={{ backgroundColor: c.value }}
            />
          ))}
        </div>

        {/* Stroke Sizes */}
        <div className="flex items-center gap-1.5 border-l border-r border-slate-200 px-3 py-0.5">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">
            Size:
          </span>
          {strokeSizes.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setStrokeWidth(s.value)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all ${
                strokeWidth === s.value
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Eraser & Clear */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsEraser(!isEraser)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
              isEraser
                ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
            }`}
          >
            <Eraser className="w-3.5 h-3.5" />
            {isEraser ? "Eraser Active" : "Eraser"}
          </button>

          <button
            type="button"
            onClick={clearCanvas}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear
          </button>
        </div>
      </div>

      {/* Interactive Canvas Box */}
      <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-inner relative group">
        <canvas
          ref={canvasRef}
          width={850}
          height={480}
          className="w-full h-auto touch-none cursor-crosshair bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute bottom-2 right-3 text-[11px] text-slate-400 font-medium pointer-events-none bg-white/80 px-2 py-0.5 rounded border border-slate-200">
          Canvas: 850×480 px
        </div>
      </div>
    </div>
  );
};

export default DrawingCanvas;
